import React, { useEffect, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";

import AssignRoleToUser from "./AssignRoleToUser";
import useRoles from "hooks/access-control/useRoles";
import usePrivileges from "hooks/access-control/usePrivileges";
import AssignPrivilegesToUser from "./AssignPrivilegesToUser";
import { toast } from "react-toastify";
import HeaderBar from "./HeaderBar";
import ModeSwitcher from "./ModeSwitcher";
import UserSelector from "./UserSelector";
import AssignedRoles from "./AssignedRoles";
import AssignedPrivileges from "./AssignedPrivileges";
import ActionButtons from "./ActionButtons";
import { useUserManagement } from "hooks";

export default function AssignRole() {
  const { users, loading: usersLoading } = useUserManagement();
  const { roles, fetchUserRoles, assignRolesToUser, removeRolesFromUser } =
    useRoles();
  const {
    fetchUserPrivileges,
    assignPrivilegesToUser,
    removePrivilegesFromUser,
  } = usePrivileges();
  const [userAssignedRoles, setUserAssignedRoles] = useState([]);
  const [preSelectedRoles, setPreSelectedRoles] = useState([]);
  const [backupRoles, setBackupRoles] = useState([]);
  const [originalRoleIds, setOriginalRoleIds] = useState([]);
  const [userAssignedPrivileges, setUserAssignedPrivileges] = useState([]);
  const [preSelectedPrivileges, setPreSelectedPrivileges] = useState([]);
  const [backupPrivileges, setBackupPrivileges] = useState([]);
  const [originalPrivilegeIds, setOriginalPrivilegeIds] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [mode, setMode] = useState("roles");
  const [removedItemIds, setRemovedItemIds] = useState([]);
  const [enableEditRole, setEnableEditRole] = useState(false);
  const [enableEditPriv, setEnableEditPriv] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form fields
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedPrivileges, setSelectedPrivileges] = useState([]);

  useEffect(() => {
    // fetch all user assigned roles
    const fetchAssignedUserRoles = async () => {
      if (!selectedUser) return;
      setIsFetching(true);
      try {
        const userRolesResponse = await fetchUserRoles(selectedUser.value);
        const mappedRoles = userRolesResponse
          .map((userRole) => roles.find((role) => role.id === userRole.roleId))
          .filter(Boolean);

        setUserAssignedRoles(mappedRoles);
        setPreSelectedRoles(
          mappedRoles?.map((role) => ({
            value: role.id,
            label: role.name,
          }))
        );
        setSelectedRoles(
          mappedRoles?.map((role) => ({
            value: role.id,
            label: role.name,
          }))
        );

        // Save original assigned role IDs for comparison while
        // revoking roles from user
        const original = mappedRoles.map((role) => role.id);
        setOriginalRoleIds(original);
      } catch (error) {
        console.log("failed to fetch user roles:", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchAssignedUserRoles();
  }, [selectedUser, fetchUserRoles, roles]);

  // fetch all user assigned privileges
  useEffect(() => {
    const loadPrivileges = async () => {
      if (!selectedUser) return;
      try {
        const res = await fetchUserPrivileges(selectedUser.value);

        const mapped = res.data.map((p) => ({
          value: p.privilege.id,
          label: p.privilege.name,
        }));

        setUserAssignedPrivileges(res.data);
        setPreSelectedPrivileges(mapped);

        // Save original assigned privilege IDs for comparison when revoking
        const original = res.data.map((p) => p.privilege.id);
        setOriginalPrivilegeIds(original);
      } catch (error) {
        console.log("Error loading privileges:", error);
      }
    };

    loadPrivileges();
  }, [selectedUser, fetchUserPrivileges, mode]);

  useEffect(() => {
    if (!selectedUser) {
      setUserAssignedRoles([]);
      setPreSelectedPrivileges([]);
      setSelectedRoles([]);
      setSelectedPrivileges([]);
      setDisabled(true);
    }
  }, [selectedUser]);

  const handleRoleChange = (newSelected) => {
    setPreSelectedRoles(newSelected);
    const currentIds = newSelected.map((r) => r.value);

    const removed = originalRoleIds.filter((id) => !currentIds.includes(id));
    setRemovedItemIds(removed);
  };

  const handlePrivilegeChange = (newSelected) => {
    setPreSelectedPrivileges(newSelected);
    const currentIds = newSelected.map((p) => p.value);

    const removed = originalPrivilegeIds.filter(
      (id) => !currentIds.includes(id)
    );
    setRemovedItemIds(removed);
  };

  // assign roles to user
  const handleSubmitRoles = async () => {
    setSubmitting(true);
    try {
      const addedRolesResponse = await assignRolesToUser(
        selectedUser.value,
        selectedRoles
      );

      const mappedRoles = addedRolesResponse
        .map((addedRole) => roles.find((role) => role.id === addedRole.roleId))
        .filter(Boolean);

      setUserAssignedRoles([...userAssignedRoles, ...mappedRoles]);

      // refresh UI list
      setPreSelectedRoles([
        ...preSelectedRoles,
        ...mappedRoles?.map((role) => ({
          value: role.id,
          label: role.name,
        })),
      ]);
      setOriginalRoleIds([
        ...originalRoleIds,
        ...mappedRoles.map((role) => role.id),
      ]);
      setDisabled(true);
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  // remove roles from user
  const handleRemoveRoles = async () => {
    setSubmitting(true);
    try {
      await removeRolesFromUser(selectedUser.value, removedItemIds);

      // refresh UI list
      const res = await fetchUserRoles(selectedUser.value);
      const mappedRoles = res
        .map((userRole) => roles.find((role) => role.id === userRole.roleId))
        .filter(Boolean);

      setUserAssignedRoles(mappedRoles);

      const original = mappedRoles.map((role) => role.id);
      setOriginalRoleIds(original);

      setRemovedItemIds([]);
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
      setEnableEditRole(false);
    }
  };

  // assign privileges to user
  const handleSubmitPrivileges = async () => {
    setSubmitting(true);
    try {
      const res = await assignPrivilegesToUser(
        selectedUser.value,
        selectedPrivileges
      );

      const mapped = res.data.map((p) => ({
        value: p.privilege.id,
        label: p.privilege.name,
      }));

      setUserAssignedPrivileges([...userAssignedPrivileges, ...res.data]);

      // refresh UI list
      setPreSelectedPrivileges([...preSelectedPrivileges, ...mapped]);
      setOriginalPrivilegeIds([
        ...originalPrivilegeIds,
        ...mapped.map((p) => p.value),
      ]);
      setDisabled(true);
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  // remove privileges from user
  const handleRemovePrivileges = async () => {
    setSubmitting(true);
    try {
      await removePrivilegesFromUser(selectedUser.value, removedItemIds);

      // refresh ui list
      const res = await fetchUserPrivileges(selectedUser.value);
      setUserAssignedPrivileges(res.data);

      const updatedOriginal = res.data.map((p) => p.privilege.id);
      setOriginalPrivilegeIds(updatedOriginal);

      setRemovedItemIds([]);
      toast.success("Privileges removed successfully");
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
      setEnableEditPriv(false);
    }
  };

  const handleEnableEdit = () => {
    if (mode === "roles") {
      setBackupRoles(preSelectedRoles);
      setEnableEditRole(true);
    } else {
      setBackupPrivileges(preSelectedPrivileges); // store original privileges
      setEnableEditPriv(true);
    }
  };

  const handleCancelEdit = () => {
    if (mode === "roles") {
      setPreSelectedRoles(backupRoles);
      setEnableEditRole(false);
      setRemovedItemIds([]);
    } else {
      setPreSelectedPrivileges(backupPrivileges); // restore original list
      setRemovedItemIds([]);
      setEnableEditPriv(false);
    }
  };

  if (usersLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <HeaderBar />

      <Row className="rounded-3 px-lg-4 relative">
        <ModeSwitcher mode={mode} setMode={setMode} />

        <UserSelector
          users={users}
          loading={usersLoading}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
        />
      </Row>

      {isFetching ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 300 }}
        >
          <Spinner animation="border" variant="success" />
          <span className="ms-3" style={{ fontSize: "13.33px" }}>
            Loading...
          </span>
        </div>
      ) : (
        <Row className="pb-lg-3 rounded-3 px-lg-4 mt-3">
          {mode === "roles" && (
            <AssignedRoles
              selectedUser={selectedUser}
              handleRoleChange={handleRoleChange}
              enableEdit={enableEditRole}
              setEnableEdit={handleEnableEdit}
              cancelEdit={handleCancelEdit}
              preSelectedRoles={preSelectedRoles}
              removedItemIds={removedItemIds}
              handleRemoveRoles={handleRemoveRoles}
              submitting={submitting}
            />
          )}

          {mode === "privileges" && (
            <AssignedPrivileges
              selectedUser={selectedUser}
              enableEdit={enableEditPriv}
              setEnableEdit={handleEnableEdit}
              cancelEdit={handleCancelEdit}
              handlePrivilegeChange={handlePrivilegeChange}
              handleRemovePrivileges={handleRemovePrivileges}
              preSelectedPrivileges={preSelectedPrivileges}
              removedItemIds={removedItemIds}
              submitting={submitting}
            />
          )}

          <ActionButtons
            mode={mode}
            selectedUser={selectedUser}
            disabled={disabled}
            setDisabled={setDisabled}
            handleSaveRoles={handleSubmitRoles}
            handleSavePrivileges={handleSubmitPrivileges}
            submitting={submitting}
          />

          <Col className="col-12">
            {mode === "roles" && (
              <AssignRoleToUser
                disabled={disabled}
                onRolesChange={(roles) => setSelectedRoles(roles)}
                assignedRoles={userAssignedRoles}
              />
            )}

            {mode === "privileges" && (
              <AssignPrivilegesToUser
                disabled={disabled}
                onPrivilegesChange={(privs) => setSelectedPrivileges(privs)}
                assignedPrivileges={userAssignedPrivileges}
              />
            )}
          </Col>
        </Row>
      )}
    </>
  );
}
